import { useState, useEffect } from 'react'
import { getMasterSheetBasedOnPropertyId, getPropertyList } from '../MasterSheetFields/Helpers/api';
import {
    Card,
    CardHeader,
    CardBody,
    Row,
    Col,
    Alert,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from 'reactstrap';
import Breadcrumb from "../../../../components/Common/Breadcrumb";
import { createColorConfig, getColorConfigsByProperty, updateColorConfig } from './Api';
import { PropertySelector } from './Components/PropertySelector';
import { ColorConfigForm } from './Components/ColorConfigForm';

// Default configuration template
const DEFAULT_COLOR_CONFIG = {
    name: "Default Color Configuration",
    colorRules: [
        {
            field: "status",
            isActive: true,
            appliesTo: "cell",
            priority: 2,
            ruleType: "conditional",
            staticColor: {
                backgroundColor: "#ffffff",
                textColor: "#000000",
                borderColor: "#cccccc"
            },
            conditions: [
                {
                    operator: "equals",
                    value: "Start Pad",
                    values: [],
                    color: {
                        backgroundColor: "#e9a5f1",
                        textColor: "#000000",
                        borderColor: "#cccccc"
                    },
                    priority: 1,
                    affectedFields: ["status"]
                },
                {
                    operator: "equals",
                    value: "Change Rent",
                    values: [],
                    color: {
                        backgroundColor: "#f07adb",
                        textColor: "#000000",
                        borderColor: "#cccccc"
                    },
                    priority: 1,
                    affectedFields: ["status"]
                },
                {
                    operator: "equals",
                    value: "Do not Renew",
                    values: [],
                    color: {
                        backgroundColor: "#ff6363",
                        textColor: "#000000",
                        borderColor: "#cccccc"
                    },
                    priority: 1,
                    affectedFields: ["status"]
                },
                {
                    operator: "equals",
                    value: "Not Renewing",
                    values: [],
                    color: {
                        backgroundColor: "#eb1e1e",
                        textColor: "#000000",
                        borderColor: "#cccccc"
                    },
                    priority: 1,
                    affectedFields: ["status"]
                },
                {
                    operator: "equals",
                    value: "Lease Renewal Sent",
                    values: [],
                    color: {
                        backgroundColor: "#fcf259",
                        textColor: "#000000",
                        borderColor: "#cccccc"
                    },
                    priority: 1,
                    affectedFields: ["status"]
                }
            ],
            ranges: []
        },
        {
            field: "targetRent",
            isActive: true,
            appliesTo: "cell",
            priority: 1,
            ruleType: "conditional",
            staticColor: {
                backgroundColor: "#ffedfa",
                textColor: "#000000",
                borderColor: "#cccccc"
            },
            conditions: [
                {
                    operator: "greater_than",
                    value: "FIELD-basicRent",
                    values: [],
                    color: {
                        backgroundColor: "#a1e3f9",
                        textColor: "#000000",
                        borderColor: "#cccccc"
                    },
                    priority: 1,
                    affectedFields: ["basicRent"]
                },
                {
                    operator: "less_than",
                    value: "FIELD-basicRent",
                    values: [],
                    color: {
                        backgroundColor: "#ffffff",
                        textColor: "#000000",
                        borderColor: "#cccccc"
                    },
                    priority: 1,
                    affectedFields: ["basicRent"]
                },
                {
                    operator: "equals",
                    value: "FIELD-basicRent",
                    values: [],
                    color: {
                        backgroundColor: "#ecfae5",
                        textColor: "#000000",
                        borderColor: "#cccccc"
                    },
                    priority: 1,
                    affectedFields: ["basicRent"]
                }
            ],
            ranges: []
        }
    ],
    isActive: true
};

const ColorConfig = () => {
    const { userData = {} } = JSON.parse(localStorage.getItem("authUser") || '{"userData": {}}');
    const [filterData, setFilterData] = useState({
        property_id: "",
        building_name: ""
    });
    const [mastersheet, setMastersheet] = useState(null);
    const [isValidSelection, setIsValidSelection] = useState(false);
    const [showColorCoding, setShowColorCoding] = useState(false);
    const [colorConfig, setColorConfig] = useState(null);
    const [existingConfigs, setExistingConfigs] = useState([]);
    const [selectedConfigForEdit, setSelectedConfigForEdit] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingConfigs, setLoadingConfigs] = useState(false);
    const [showDefaultModal, setShowDefaultModal] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [usingDefault, setUsingDefault] = useState(false);

    useEffect(() => {
        const isValid = filterData.property_id && filterData.building_name;
        setIsValidSelection(isValid);
    }, [filterData.property_id, filterData.building_name]);

    useEffect(() => {
        if (userData?.id) {
            getPropertyList(userData)
                .then((filteredData) => {
                    if (filteredData) {
                        setFilterData(prev => ({
                            ...prev,
                            ...filteredData,
                            property_id: ""
                        }));
                    }
                })
                .catch(error => console.error("Error fetching property list:", error));
        }
    }, [userData?.id]);

    // Fetch mastersheet when property selection changes
    useEffect(() => {
        if (filterData?.property_id) {
            setLoading(true);
            getMasterSheetBasedOnPropertyId({
                property_id: filterData.property_id,
                building_name: filterData.building_name
            })
                .then(({ data }) => {
                    if (data) {
                        setMastersheet(data);
                        setShowColorCoding(false);
                        setColorConfig(null);
                        setSelectedConfigForEdit(null);
                        setUsingDefault(false);
                    } else {
                        setMastersheet(null);
                    }
                })
                .catch((error) => {
                    console.error("Error fetching master sheet:", error);
                    setMastersheet(null);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [filterData?.property_id, filterData?.building_name]);

    // Fetch existing color configurations when property selection is valid
    useEffect(() => {
        if (filterData?.property_id && filterData?.building_name) {
            fetchExistingConfigs();
        } else {
            setExistingConfigs([]);
            setColorConfig(null);
            setUsingDefault(false);
        }
    }, [filterData?.property_id, filterData?.building_name]);

    const fetchExistingConfigs = async () => {
        try {
            setLoadingConfigs(true);
            const params = { building_name: filterData.building_name };
            const configsData = await getColorConfigsByProperty(filterData.property_id, params);

            if (configsData?.success && Array.isArray(configsData.data) && configsData.data.length > 0) {
                setExistingConfigs(configsData.data);
                console.log({ EXISTINGCONFIG: configsData.data })
                const activeConfig = configsData.data.find(config => config.isActive);
                setColorConfig(activeConfig || configsData.data[0] || null);
                setUsingDefault(false);
            } else {
                setExistingConfigs([]);
                setColorConfig(null);
                setUsingDefault(false);
            }
        } catch (error) {
            console.error("Error fetching existing color configurations:", error);
            setExistingConfigs([]);
            setColorConfig(null);
            setUsingDefault(false);
        } finally {
            setLoadingConfigs(false);
        }
    };

    const handleSetupColorCoding = () => {
        setSelectedConfigForEdit(null);
        setShowColorCoding(true);
    };

    const handleEditColorConfig = (config) => {
        setSelectedConfigForEdit(config);
        setShowColorCoding(true);
    };

    const handleUseDefaultConfig = () => {
        setShowDefaultModal(true);
    };

    const handleConfirmUseDefault = () => {
        setColorConfig({
            ...DEFAULT_COLOR_CONFIG,
            _id: 'default-config',
            property_id: filterData.property_id,
            building_name: filterData.building_name,
            mastersheet_id: mastersheet?._id
        });
        setUsingDefault(true);
        setShowDefaultModal(false);
    };

    const handleResetToDefault = () => {
        setShowResetModal(true);
    };

    const handleConfirmResetToDefault = () => {
        setColorConfig({
            ...DEFAULT_COLOR_CONFIG,
            _id: 'default-config',
            property_id: filterData.property_id,
            building_name: filterData.building_name,
            mastersheet_id: mastersheet?._id
        });
        setUsingDefault(true);
        setShowResetModal(false);
    };

    const handleSaveDefaultAsNew = async () => {
        try {
            setLoading(true);
            const configData = {
                property_id: filterData.property_id,
                building_name: filterData.building_name,
                mastersheet_id: mastersheet?._id,
                ...DEFAULT_COLOR_CONFIG
            };

            await createColorConfig(configData);
            await fetchExistingConfigs();
            setUsingDefault(false);
        } catch (error) {
            console.error("Error saving default configuration:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateOrUpdateColorConfig = async (configData) => {
        try {
            setLoading(true);

            let newConfig;
            if (selectedConfigForEdit?._id && selectedConfigForEdit._id !== 'default-config') {
                newConfig = await updateColorConfig(selectedConfigForEdit._id, {
                    property_id: filterData.property_id,
                    building_name: filterData.building_name,
                    mastersheet_id: mastersheet?._id,
                    ...configData
                });
            } else {
                newConfig = await createColorConfig({
                    property_id: filterData.property_id,
                    building_name: filterData.building_name,
                    mastersheet_id: mastersheet?._id,
                    ...configData
                });
            }

            // Refresh the configurations list
            await fetchExistingConfigs();

            setShowColorCoding(false);
            setSelectedConfigForEdit(null);
            setUsingDefault(false);
        } catch (error) {
            console.error("Error creating/updating color configuration:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectConfig = (config) => {
        setColorConfig(config);
        setUsingDefault(false);
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Breadcrumb title="Home" breadcrumbItem="Master Sheet" />
                <Row>
                    <Col xl={12}>
                        <Card>
                            <CardHeader>
                                <PropertySelector
                                    userData={userData}
                                    filterData={filterData}
                                    setFilterData={setFilterData}
                                />
                            </CardHeader>
                            <CardBody>
                                {/* Property Selection Validation */}
                                {!isValidSelection && (
                                    <Alert color="danger" className="mt-3 text-center">
                                        <strong>⚠️ Property Name and Building must be selected!</strong>
                                    </Alert>
                                )}

                                {/* Loading State */}
                                {loading && isValidSelection && (
                                    <div className="text-center py-4">
                                        <div className="spinner-border" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading mastersheet data...</p>
                                    </div>
                                )}

                                {/* Loading Configurations */}
                                {loadingConfigs && isValidSelection && (
                                    <div className="text-center py-2">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                            <span className="sr-only">Loading configurations...</span>
                                        </div>
                                        <small className="text-muted ms-2">Loading color configurations...</small>
                                    </div>
                                )}

                                {/* No Mastersheet Available */}
                                {isValidSelection && !loading && mastersheet === null && (
                                    <>
                                        <Alert color="warning" className="d-flex align-items-center justify-content-between p-3 border border-warning rounded">
                                            <div className="d-flex align-items-center">
                                                <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                                                <strong>⚠️ No Mastersheet Available for this Property</strong>
                                            </div>
                                        </Alert>

                                        <Alert color="info" className="mt-3">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div>
                                                    <i className="bi bi-info-circle-fill me-2"></i>
                                                    <strong>Color Coding Setup</strong>
                                                    <p className="mb-0 mt-1">
                                                        Color coding can only be configured after creating a mastersheet for this property.
                                                    </p>
                                                </div>
                                            </div>
                                        </Alert>
                                    </>
                                )}

                                {isValidSelection && !loading && mastersheet && (
                                    <div>
                                        <div className="mastersheet-section">
                                            <Alert color="success" className="mb-3">
                                                <i className="bi bi-check-circle-fill me-2"></i>
                                                <strong>Mastersheet Available</strong> - Layout is ready for this property.
                                            </Alert>
                                        </div>

                                        {/* Color Coding Section */}
                                        <div className="color-coding-section mt-4">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <h5 className="mb-0">
                                                    <i className="bi bi-palette me-2"></i>
                                                    Color Coding Configuration
                                                </h5>
                                                {!showColorCoding && (
                                                    <div className="d-flex gap-2">
                                                        {existingConfigs.length === 0 && !usingDefault && (
                                                            <Button
                                                                color="info"
                                                                onClick={handleUseDefaultConfig}
                                                                className="fw-bold"
                                                            >
                                                                <i className="bi bi-gear-fill me-1"></i>
                                                                Use Default Config
                                                            </Button>
                                                        )}
                                                        {(existingConfigs.length > 0 || usingDefault) && (
                                                            <Button
                                                                color="outline-secondary"
                                                                onClick={handleResetToDefault}
                                                                className="fw-bold"
                                                            >
                                                                <i className="bi bi-arrow-clockwise me-1"></i>
                                                                Reset to Default
                                                            </Button>
                                                        )}
                                                        <Button
                                                            color="primary"
                                                            onClick={handleSetupColorCoding}
                                                            className="fw-bold"
                                                        >
                                                            <i className="bi bi-plus-lg me-1"></i>
                                                            {existingConfigs.length > 0 || usingDefault ? 'Create New Config' : 'Setup Color Coding'}
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Default Configuration Notice */}
                                            {usingDefault && !showColorCoding && (
                                                <Alert color="info" className="mb-3">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div>
                                                            <i className="bi bi-info-circle-fill me-2"></i>
                                                            <strong>Using Default Configuration</strong>
                                                            <p className="mb-0 mt-1">
                                                                You're currently using the default color configuration. You can save it as a custom configuration or create a new one.
                                                            </p>
                                                        </div>
                                                        <Button
                                                            color="outline-primary"
                                                            size="sm"
                                                            onClick={handleSaveDefaultAsNew}
                                                            disabled={loading}
                                                        >
                                                            <i className="bi bi-save me-1"></i>
                                                            Save as Custom
                                                        </Button>
                                                    </div>
                                                </Alert>
                                            )}

                                            {/* Existing Configurations List */}
                                            {existingConfigs.length > 0 && !showColorCoding && (
                                                <Card className="mb-3">
                                                    <CardBody>
                                                        <h6 className="mb-3">
                                                            <i className="bi bi-list-ul me-2"></i>
                                                            Existing Configurations ({existingConfigs.length})
                                                        </h6>
                                                        <div className="row">
                                                            {existingConfigs.map((config, index) => (
                                                                <div key={config.id || config._id || index} className="col-md-6 col-lg-4 mb-3">
                                                                    <div className={`card h-100 ${(colorConfig?.id === config.id || colorConfig?._id === config._id) && !usingDefault ? 'border-primary' : ''}`}>
                                                                        <div className="card-body">
                                                                            <div className="d-flex align-items-start justify-content-between mb-2">
                                                                                <h6 className="card-title mb-1">{config.name}</h6>
                                                                                <span className={`badge ${config.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                                                    {config.isActive ? 'Active' : 'Inactive'}
                                                                                </span>
                                                                            </div>
                                                                            <p className="card-text text-muted small mb-2">
                                                                                {config.colorRules?.length || 0} rules configured
                                                                            </p>
                                                                            <div className="d-flex gap-2">
                                                                                <Button
                                                                                    color="outline-primary"
                                                                                    size="sm"
                                                                                    onClick={() => handleSelectConfig(config)}
                                                                                    className={(colorConfig?.id === config.id || colorConfig?._id === config._id) && !usingDefault ? 'd-none' : ''}
                                                                                >
                                                                                    Select
                                                                                </Button>
                                                                                <Button
                                                                                    color="outline-secondary"
                                                                                    size="sm"
                                                                                    onClick={() => handleEditColorConfig(config)}
                                                                                >
                                                                                    <i className="bi bi-pencil me-1"></i>
                                                                                    Edit
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            )}

                                            {/* Show Color Config Form */}
                                            {showColorCoding && (
                                                <Card>
                                                    <CardBody>
                                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                                            <h6 className="mb-0">
                                                                {selectedConfigForEdit ?
                                                                    `Edit Configuration: ${selectedConfigForEdit.name}` :
                                                                    'Create New Color Configuration'
                                                                }
                                                            </h6>
                                                        </div>
                                                        <ColorConfigForm
                                                            mastersheet={mastersheet}
                                                            property_id={filterData?.property_id || ''}
                                                            building_name={filterData?.building_name || ''}
                                                            onSave={handleCreateOrUpdateColorConfig}
                                                            onCancel={() => {
                                                                setShowColorCoding(false);
                                                                setSelectedConfigForEdit(null);
                                                            }}
                                                            loading={loading}
                                                            existingConfig={selectedConfigForEdit}
                                                        />
                                                    </CardBody>
                                                </Card>
                                            )}

                                            {/* Show Selected Color Config Details */}
                                            {colorConfig && !showColorCoding && !usingDefault && (
                                                <Card>
                                                    <CardBody>
                                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                                            <div>
                                                                <h6 className="mb-1">
                                                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                                                    {colorConfig.name}
                                                                </h6>
                                                                <small className="text-muted">
                                                                    {colorConfig.colorRules?.length || 0} rules configured
                                                                    {colorConfig.isActive && <span className="badge bg-success ms-2">Active</span>}
                                                                </small>
                                                            </div>
                                                            <Button
                                                                color="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleEditColorConfig(colorConfig)}
                                                            >
                                                                <i className="bi bi-pencil me-1"></i>
                                                                Edit
                                                            </Button>
                                                        </div>

                                                        {/* Preview of color rules */}
                                                        <div className="color-rules-preview">
                                                            <h6 className="text-muted mb-2">Color Rules Preview:</h6>
                                                            {colorConfig.colorRules?.map((rule, index) => (
                                                                <div key={index} className="border rounded p-2 mb-2 d-flex align-items-center justify-content-between">
                                                                    <div>
                                                                        <small>
                                                                            <strong>{rule.field}</strong> - {rule.ruleType} rule
                                                                            {rule.appliesTo && <span className="text-muted"> (applies to {rule.appliesTo})</span>}
                                                                        </small>
                                                                    </div>
                                                                    <span className={`badge ${rule.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                                        {rule.isActive ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            )}

                                            {/* Show Default Config Details when using default */}
                                            {usingDefault && colorConfig && !showColorCoding && (
                                                <Card className="border-info">
                                                    <CardBody>
                                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                                            <div>
                                                                <h6 className="mb-1">
                                                                    <i className="bi bi-gear-fill text-info me-2"></i>
                                                                    {colorConfig.name} (Default)
                                                                </h6>
                                                                <small className="text-muted">
                                                                    {colorConfig.colorRules?.length || 0} rules configured
                                                                    <span className="badge bg-info ms-2">Default Config</span>
                                                                </small>
                                                            </div>
                                                            <div className="d-flex gap-2">
                                                                <Button
                                                                    color="outline-info"
                                                                    size="sm"
                                                                    onClick={() => handleEditColorConfig(colorConfig)}
                                                                >
                                                                    <i className="bi bi-pencil me-1"></i>
                                                                    Edit & Save as New
                                                                </Button>
                                                            </div>
                                                        </div>

                                                        {/* Preview of default color rules */}
                                                        <div className="color-rules-preview">
                                                            <h6 className="text-muted mb-2">Default Color Rules Preview:</h6>
                                                            {colorConfig.colorRules?.map((rule, index) => (
                                                                <div key={index} className="border rounded p-2 mb-2 d-flex align-items-center justify-content-between">
                                                                    <div>
                                                                        <small>
                                                                            <strong>{rule.field}</strong> - {rule.ruleType} rule
                                                                            {rule.appliesTo && <span className="text-muted"> (applies to {rule.appliesTo})</span>}
                                                                        </small>
                                                                    </div>
                                                                    <span className={`badge ${rule.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                                                        {rule.isActive ? 'Active' : 'Inactive'}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            )}

                                            {/* No color config and not showing form and not using default */}
                                            {existingConfigs.length === 0 && !colorConfig && !showColorCoding && !loadingConfigs && !usingDefault && (
                                                <Alert color="light" className="text-center">
                                                    <i className="bi bi-palette me-2"></i>
                                                    No color coding configuration found. You can use the default configuration or create a new one.
                                                </Alert>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Default Configuration Confirmation Modal */}
                <Modal isOpen={showDefaultModal} toggle={() => setShowDefaultModal(false)}>
                    <ModalHeader toggle={() => setShowDefaultModal(false)}>
                        Use Default Configuration
                    </ModalHeader>
                    <ModalBody>
                        <div className="mb-3">
                            <p>Are you sure you want to use the default color configuration? This will apply the following rules:</p>
                            <ul className="text-muted small">
                                <li><strong>Status Field:</strong> 5 conditional color rules for different lease statuses</li>
                                <li><strong>Target Rent Field:</strong> 3 conditional color rules comparing with basic rent</li>
                            </ul>
                            <p className="text-info small">
                                <i className="bi bi-info-circle me-1"></i>
                                You can modify these rules later or save them as a custom configuration.
                            </p>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="info" onClick={handleConfirmUseDefault}>
                            <i className="bi bi-check-lg me-1"></i>
                            Yes, Use Default
                        </Button>
                        <Button color="secondary" onClick={() => setShowDefaultModal(false)}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Reset to Default Confirmation Modal */}
                <Modal isOpen={showResetModal} toggle={() => setShowResetModal(false)}>
                    <ModalHeader toggle={() => setShowResetModal(false)}>
                        Reset to Default Configuration
                    </ModalHeader>
                    <ModalBody>
                        <div className="mb-3">
                            <p>Are you sure you want to reset to the default color configuration? This will:</p>
                            <ul className="text-muted small">
                                <li>Replace your current configuration with the default setup</li>
                                <li>Apply default rules for Status and Target Rent fields</li>
                                <li>Any unsaved changes will be lost</li>
                            </ul>
                            <p className="text-warning small">
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                This action will temporarily switch to default mode. You can save it as a new configuration if needed.
                            </p>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="warning" onClick={handleConfirmResetToDefault}>
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            Yes, Reset to Default
                        </Button>
                        <Button color="secondary" onClick={() => setShowResetModal(false)}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Modal>
            </div>
        </div>
    )
}

export { ColorConfig }